import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Card, Button, Container } from "react-bootstrap";
import { getOffer } from "../../actions/AllOffersActions";
import { imgUrl } from "src/actions/types";
import * as UI from '../ui-components';

const ViewAllOffersPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("latest");
  const [currentTime, setCurrentTime] = useState(new Date());

  // Internal CSS styles
  const styles = {
    container: {
      maxWidth: 1200
    },
    card: {
      // Removed maxHeight constraint to allow content to expand naturally
      minHeight: "400px"
    },
    cardImage: {
      height: "160px",
      width: "100%",
      objectFit: "cover",
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    },
    title: {
      fontSize: "1rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    assignTypeBadge: {
      fontSize: "0.7rem",
      maxWidth: "80px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    activeBadge: {
      fontSize: "0.7rem"
    },
    description: {
      fontSize: "0.85rem",
      // Removed line clamp and overflow restrictions
      lineHeight: "1.4",
      wordWrap: "break-word"
    },
    criteriaText: {
      fontSize: "0.85rem",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden"
    },
    daysRemaining: {
      fontSize: "0.75rem"
    },
    countdown: {
      fontSize: "0.7rem"
    }
  };

  // Reward option arrays
  const rewardForPatientOptions = [
    { value: "", label: "None" },
    { value: "onCountAppointments", label: "On count of appointments" },
    { value: "onFamilyCount", label: "On family count" },
    { value: "onRevenueAppointments", label: "On revenue of appointments" },
  ];

  const rewardForDoctorOptions = [
    { value: "", label: "None" },
    { value: "numAppointments", label: "No. of Appointments" },
    { value: "revenueAppointments", label: "Revenue of Appointments" },
    { value: "highestViewsArticles", label: "Highest Views on Articles" },
    { value: "averageViewsArticles", label: "Average Views on Articles" },
  ];
  const handleEdit = () => {
    console.log('Edit user');
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure?')) {
      console.log('Delete user');
    }
  };
  // Update current time every second for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Optimized image URL handler
  const getOfferImage = (offerImage) => {
    if (offerImage && offerImage.trim() !== "") {
      try {
        // Handle both absolute URLs and relative paths
        if (offerImage.startsWith('http')) {
          return offerImage;
        }
        
        // Construct URL from base imgUrl
        const baseUrl = imgUrl.endsWith("/") ? imgUrl.slice(0, -1) : imgUrl;
        const imagePath = offerImage.startsWith("/") ? offerImage.slice(1) : offerImage;
        return `${baseUrl}/${imagePath}`;
      } catch (error) {
        console.warn("Error constructing image URL:", error);
        return "/assets/img/offers/default_offer_image.jpg";
      }
    }
    // Return default image when no image is provided
    return "/assets/img/offers/default_offer_image.jpg";
  };

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dispatch(getOffer());
        
        if (response && response.data) {
          const currentDate = new Date();
          
          // Filter offers: status = 'publish' (case-insensitive) and within validity period
          const validOffers = response.data.filter(offer => {
            // Check if status is 'publish' (case-insensitive)
            if (!offer.status || offer.status.toLowerCase() !== "publish") {
              console.log(`Offer ${offer.id} filtered out - status: ${offer.status}`);
              return false;
            }
            
            // Validate dates
            if (!offer.startDate || !offer.endDate) {
              console.log(`Offer ${offer.id} filtered out - missing dates`);
              return false;
            }
            
            const startDate = new Date(offer.startDate);
            const endDate = new Date(offer.endDate);
            // Set end date to end of day to include the entire end date
            endDate.setHours(23, 59, 59, 999);
            
            // Check if current date is within offer period
            const isActive = currentDate >= startDate && currentDate <= endDate;
            console.log(`Offer ${offer.id}: ${offer.title}, start: ${startDate}, end: ${endDate}, current: ${currentDate}, active: ${isActive}`);
            
            return isActive;
          });
          
          setOffers(validOffers);
          console.log(`Loaded ${validOffers.length} valid offers`, validOffers);
        } else {
          setOffers([]);
        }
      } catch (error) {
        console.error("Failed to fetch offers", error);
        setError("Failed to load offers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [dispatch]);

  const filteredOffers = useMemo(() => {
    console.log(`[useMemo] Filtering ${offers.length} offers`);
    let filtered = offers;

    // Search filter
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (offer) =>
          offer.title?.toLowerCase().includes(lower) ||
          offer.description?.toLowerCase().includes(lower) ||
          offer.rewardDetails?.toLowerCase().includes(lower) ||
          offer.rewardType?.toLowerCase().includes(lower)
      );
    }

    // Date sorting - prioritize createdAt, fallback to startDate
    filtered = filtered.slice().sort((a, b) => {
      const dateA = new Date(a.createdAt || a.startDate);
      const dateB = new Date(b.createdAt || b.startDate);
      
      if (dateFilter === "latest") {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });

    return filtered;
  }, [offers, searchTerm, dateFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const isOfferActive = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Set end date to end of day for consistent comparison
      end.setHours(23, 59, 59, 999);
      return currentTime >= start && currentTime <= end;
    } catch (error) {
      return false;
    }
  };

  const getTimeRemaining = (endDate) => {
    if (!endDate) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    try {
      const end = new Date(endDate);
      // Set end date to end of day for accurate calculation
      end.setHours(23, 59, 59, 999);
      const diffTime = end - currentTime;
      
      if (diffTime <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffTime % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds };
    } catch (error) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  };

  const formatTimeRemaining = (endDate) => {
    const { days, hours, minutes, seconds } = getTimeRemaining(endDate);
    
    if (days > 1) {
      return `${days} days remaining`;
    } else if (days === 1) {
      return `1 day remaining`;
    } else {
      // Less than 1 day remaining - show detailed countdown
      if (hours > 0 || minutes > 0 || seconds > 0) {
        return `${hours} hrs ${minutes} min ${seconds} sec remaining`;
      } else {
        return "Expired";
      }
    }
  };

  const getApplicableForText = (offer) => {
    if (!offer.assignedType) return "For All";
    
    const assignedType = offer.assignedType.toLowerCase();
    if (assignedType === "patient") return "For Patients";
    if (assignedType === "doctor") return "For Doctors";
    if (assignedType === "both") return "For Patients & Doctors";
    return "For All";
  };

  const getRewardCriteriaText = (offer) => {
    if (!offer.assignedType) return "";
    
    const assignedType = offer.assignedType.toLowerCase();
    
    if (assignedType === "patient" && offer.rewardForPatient) {
      const option = rewardForPatientOptions.find(opt => opt.value === offer.rewardForPatient);
      return option ? option.label : "";
    }
    
    if ((assignedType === "doctor" || assignedType === "both") && offer.rewardForDoctor) {
      const option = rewardForDoctorOptions.find(opt => opt.value === offer.rewardForDoctor);
      return option ? option.label : "";
    }
    
    return "";
  };

  return (
    <div>
      <div className="breadcrumb-bar mb-3">
        <div className="container-fluid">
          <nav aria-label="breadcrumb" className="page-breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/">Home</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                View Offers
              </li>
            </ol>
          </nav>
          <h6 className="breadcrumb-title">View Offers</h6>
        </div>
      </div>

      <div className="offers-page container my-5" style={styles.container}>
        <Helmet>
          <title>MediSevas | Special Offers</title>
          <meta name="description" content="Exclusive offers and discounts for patients and doctors at MediSevas." />
        </Helmet>

        <div className="container text-center mb-4">
          <h1 className="display-5 fw-bold">Special Offers</h1>
          <p className="mt-2 lead">
            Exclusive discounts and promotions for our valued patients and doctors
          </p>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-4 d-flex justify-content-center align-items-center gap-3">
          <input
            type="search"
            className="form-control shadow-sm"
            style={{ maxWidth: 400 }}
            placeholder="Search offers by title, description, or reward type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search offers"
          />
          <select
            className="form-select shadow-sm"
            style={{ maxWidth: 150 }}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            aria-label="Sort by date"
          >
            <option value="latest">Date: Latest</option>
            <option value="oldest">Date: Oldest</option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading offers...</span>
            </div>
            <p className="mt-2 text-muted">Loading offers...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-danger text-center" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* No Offers State */}
        {!loading && !error && filteredOffers.length === 0 && (
          <div className="alert alert-info text-center">
            <i className="fas fa-info-circle me-2"></i>
            {offers.length === 0 
              ? "No active offers available at the moment. Please check back later." 
              : "No offers found matching your search criteria."}
          </div>
        )}

        {/* Offers Grid */}
        <div className="row gy-3">
          {filteredOffers.map((offer) => (
            <div
              key={offer.id || offer._id}
              className="col-sm-12 col-md-6 col-lg-4 mb-3"
            >
              <div className="card h-100 shadow border-0 rounded-3 offer-card" style={styles.card}>
                {/* Image Section - displays whatever image is available */}
                <div className="card-image-container">
                  <img
                    src={getOfferImage(offer.image)}
                    alt={offer.title || "Special Offer"}
                    className="card-img-top rounded-top"
                    style={styles.cardImage}
                    onError={(e) => {
                      // Fallback to default image if the image fails to load
                      e.target.src = "/assets/img/offers/default_offer_image.jpg";
                    }}
                  />
                </div>
                
                <div className="card-body d-flex flex-column p-3">
                  {/* Top Row: Title (left), Assign Type (center), Active Status (right) */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    {/* Title - Left aligned with truncation */}
                    <h6 
                      className="card-title text-dark fw-bold mb-0 me-2 flex-grow-1" 
                      style={styles.title}
                      title={offer.title || "Untitled Offer"}
                    >
                      {offer.title || "Untitled Offer"}
                    </h6>
                    
                    {/* Assign Type - Center aligned with truncation */}
                    <span 
                      className="badge bg-info mx-2 text-nowrap" 
                      style={styles.assignTypeBadge}
                      title={getApplicableForText(offer)}
                    >
                      {getApplicableForText(offer)}
                    </span>
                    
                    {/* Active Status - Right aligned */}
                    {isOfferActive(offer.startDate, offer.endDate) && (
                      <span className="badge bg-success ms-2 flex-shrink-0" style={styles.activeBadge}>
                        Active
                      </span>
                    )}
                  </div>
                  
                  {/* Offer Description - Full text display without truncation */}
                  <div className="mb-3 flex-grow-1">
                    <p 
                      className="card-text text-muted small" 
                      style={styles.description}
                    >
                      {offer.description || "No description available."}
                    </p>
                  </div>

                  {/* Criteria Section */}
                  <div className="mb-3">
                    <strong className="small d-block mb-1">Criteria:</strong>
                    <span 
                      className="text-muted small"
                      style={styles.criteriaText}
                    >
                      {getRewardCriteriaText(offer) || "No specific criteria"}
                    </span>
                  </div>

                  {/* Date Information - Fixed layout */}
                  <div className="mt-auto pt-2 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      {/* Left side: Valid until date */}
                      <div className="d-flex align-items-center">
                        <small className="text-muted me-1">Valid until:</small>
                        <strong className="small">{formatDate(offer.endDate)}</strong>
                      </div>
                      
                      {/* Right side: Time remaining */}
                      <div>
                        {isOfferActive(offer.startDate, offer.endDate) && (
                          <small 
                            className="text-warning fw-semibold" 
                            style={getTimeRemaining(offer.endDate).days > 0 ? styles.daysRemaining : styles.countdown}
                          >
                            {formatTimeRemaining(offer.endDate)}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              
              </div>
            </div>
          ))}
        </div>
      </div>






    </div>
  );
};

export default ViewAllOffersPage;
